import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { DealContacts } from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import type { ContactCreate, DealContact } from "@/api/types";
import ContactForm from "@/components/ContactForm";
import { confirmDialog, toast } from "@/lib/feedback";

interface ContactsSectionProps {
  dealId: string;
}

type AttachPayload = {
  contact_id?: string;
  contact?: ContactCreate;
  relationship?: string | null;
};

function ContactRow({
  link,
  onDetach,
}: {
  link: DealContact;
  onDetach: (link: DealContact) => void;
}) {
  const { contact, relationship } = link;
  const isBroker = contact.kind === "broker_contact";

  return (
    <li className="panel-tight p-2.5 flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-md font-medium truncate">{contact.name}</span>
          {contact.title && <span className="text-sm text-mut-300">{contact.title}</span>}
          {isBroker && (
            <span
              className={`chip ${
                contact.affiliation === "internal"
                  ? "text-accent-azure bg-accent-azure/10"
                  : "text-mut-300 bg-fg/[0.06]"
              }`}
            >
              {contact.affiliation === "internal" ? "Internal" : "External"}
            </span>
          )}
        </div>
        {contact.email && <div className="text-xs text-mut-500 truncate">{contact.email}</div>}
        {contact.phone && <div className="text-xs text-mut-500">{contact.phone}</div>}
        {relationship && <div className="text-xs text-mut-300 mt-0.5">{relationship}</div>}
      </div>
      <button
        type="button"
        className="btn btn-ghost shrink-0"
        onClick={() => onDetach(link)}
        aria-label={`Unlink ${contact.name}`}
        title="Unlink"
      >
        <X className="w-4 h-4" />
      </button>
    </li>
  );
}

export default function ContactsSection({ dealId }: ContactsSectionProps) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const contactsQuery = useQuery({
    queryKey: ["deal-contacts", dealId],
    queryFn: () => DealContacts.list(dealId),
    enabled: !!dealId,
  });

  const attachMutation = useMutation({
    mutationFn: (payload: AttachPayload) => DealContacts.attach(dealId, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["deal-contacts", dealId] });
      void qc.invalidateQueries({ queryKey: ["deal-people", dealId] });
      void qc.invalidateQueries({ queryKey: ["contacts"] });
      setAdding(false);
    },
    onError: (e) => toast.err(`Could not add contact: ${apiErrorMessage(e)}`),
  });

  const detachMutation = useMutation({
    mutationFn: (contactId: string) => DealContacts.detach(dealId, contactId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["deal-contacts", dealId] });
      void qc.invalidateQueries({ queryKey: ["deal-people", dealId] });
    },
    onError: (e) => toast.err(`Could not unlink contact: ${apiErrorMessage(e)}`),
  });

  const onDetach = async (link: DealContact) => {
    if (
      await confirmDialog({
        title: `Unlink ${link.contact.name}?`,
        body: "They stay in Contacts; they're just no longer on this deal.",
        confirmLabel: "Unlink",
      })
    ) {
      detachMutation.mutate(link.contact.id);
    }
  };

  const links = contactsQuery.data ?? [];
  const accountContacts = links.filter((l) => l.contact.kind === "account_contact");
  const brokers = links.filter((l) => l.contact.kind === "broker_contact");

  const renderGroup = (heading: string, group: DealContact[]) => (
    <div>
      <div className="label">{heading}</div>
      {group.length === 0 ? (
        <div className="text-sm text-mut-500 italic">None yet</div>
      ) : (
        <ul className="space-y-1.5">
          {group.map((link) => (
            <ContactRow key={link.id} link={link} onDetach={onDetach} />
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-md font-semibold">Contacts</h2>
        {!adding && (
          <button type="button" className="btn btn-ghost" onClick={() => setAdding(true)}>
            <Plus className="w-3.5 h-3.5" /> Add contact
          </button>
        )}
      </div>

      {adding && (
        <ContactForm
          onSubmit={(p) => attachMutation.mutate(p)}
          onCancel={() => setAdding(false)}
          submitting={attachMutation.isPending}
        />
      )}

      {contactsQuery.isLoading ? (
        <div className="text-md text-mut-300">Loading…</div>
      ) : (
        <div className="space-y-4">
          {renderGroup("Account contacts", accountContacts)}
          {renderGroup("Brokers", brokers)}
        </div>
      )}
    </div>
  );
}
